import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  WorkshopInvoice,
  CompanyInfo,
  InvoiceItem,
  Workshop,
  WorkshopParticipant,
} from '../types';
import { cryptoService } from './cryptoService';
import { invoiceService } from './invoiceService';

export class WorkshopInvoiceService {
  private static instance: WorkshopInvoiceService;

  public static getInstance(): WorkshopInvoiceService {
    if (!WorkshopInvoiceService.instance) {
      WorkshopInvoiceService.instance = new WorkshopInvoiceService();
    }
    return WorkshopInvoiceService.instance;
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getClientIP(): string {
    // En producción, esto debería obtenerse del servidor
    return typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  }

  // Crear una nueva factura de taller
  async createWorkshopInvoice(
    userId: string,
    workshop: Workshop,
    participant: WorkshopParticipant,
    companyInfo: CompanyInfo,
    items: Omit<InvoiceItem, 'id' | 'subtotal' | 'taxAmount' | 'total'>[],
    options?: {
      isPaid?: boolean;
      paymentMethod?: string;
      stripePaymentIntentId?: string;
      stripeSessionId?: string;
    }
  ): Promise<WorkshopInvoice> {
    try {
      // Asegurar que todos los items tengan taxRate: 0 (exentos de IVA)
      const itemsWithZeroTax = items.map(item => ({
        ...item,
        taxRate: 0, // Talleres exentos de IVA
      }));

      // Calcular totales (sin IVA)
      const calculatedItems = itemsWithZeroTax.map(item => {
        const subtotal =
          item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100);
        const taxAmount = 0; // Siempre 0 para talleres
        const total = subtotal; // Total = subtotal (sin IVA)

        return {
          ...item,
          id: this.generateId(),
          subtotal: Math.round(subtotal * 100) / 100,
          taxAmount: 0,
          total: Math.round(total * 100) / 100,
        };
      });

      const subtotal = calculatedItems.reduce(
        (sum, item) => sum + item.subtotal,
        0
      );
      const taxAmount = 0; // Siempre 0 para talleres
      const total = subtotal; // Total = subtotal (sin IVA)

      // Generar número de factura usando el servicio compartido
      const invoiceNumber = await invoiceService.getNextInvoiceNumber(userId);

      // Crear factura
      const invoiceDate = new Date();
      const invoice: Omit<WorkshopInvoice, 'id'> = {
        userId,
        workshopId: workshop.id!,
        participantId: participant.id!,
        invoiceNumber,
        invoiceDate,
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 días
        participantInfo: {
          name: participant.participantName,
          surname: participant.participantSurname,
          email: participant.participantEmail,
          dni: participant.participantDni,
          address: participant.participantAddress,
          city: participant.participantCity,
          postalCode: participant.participantPostalCode,
          province: participant.participantProvince,
        },
        workshopInfo: {
          title: workshop.title,
          date: workshop.date,
          location: workshop.location,
        },
        companyInfo,
        items: calculatedItems,
        subtotal: Math.round(subtotal * 100) / 100,
        taxAmount: 0,
        total: Math.round(total * 100) / 100,
        currency: 'EUR',
        status: options?.isPaid ? 'paid' : 'sent',
        paymentMethod: options?.paymentMethod,
        paymentDate: options?.isPaid ? invoiceDate : undefined,
        stripePaymentIntentId: options?.stripePaymentIntentId,
        stripeSessionId: options?.stripeSessionId,
        auditTrail: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Generar hash de la factura completa
      const invoiceForHash: Omit<
        WorkshopInvoice,
        'id' | 'auditTrail' | 'createdAt' | 'updatedAt'
      > = {
        userId: invoice.userId,
        workshopId: invoice.workshopId,
        participantId: invoice.participantId,
        invoiceNumber: invoice.invoiceNumber,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        participantInfo: invoice.participantInfo,
        workshopInfo: invoice.workshopInfo,
        companyInfo: invoice.companyInfo,
        items: invoice.items,
        subtotal: invoice.subtotal,
        taxAmount: invoice.taxAmount,
        total: invoice.total,
        currency: invoice.currency,
        status: invoice.status,
        paymentMethod: invoice.paymentMethod,
        paymentDate: invoice.paymentDate,
        stripePaymentIntentId: invoice.stripePaymentIntentId,
        stripeSessionId: invoice.stripeSessionId,
        xmlContent: invoice.xmlContent,
        pdfUrl: invoice.pdfUrl,
        notes: invoice.notes,
        terms: invoice.terms,
      };
      const invoiceHash = await cryptoService.generateWorkshopInvoiceHash({
        ...invoiceForHash,
        id: '',
        auditTrail: [],
        createdAt: invoice.createdAt,
        updatedAt: invoice.updatedAt,
      } as WorkshopInvoice);

      // Crear log de auditoría criptográficamente seguro
      const auditLog = await cryptoService.createSecureAuditLog(
        'created',
        userId,
        '', // Se actualizará después
        `Factura de taller ${invoiceNumber} creada para ${participant.participantName} ${participant.participantSurname} - ${workshop.title}`,
        this.getClientIP(),
        navigator.userAgent,
        '' // Primer evento, no tiene hash anterior
      );

      // Reemplazar el hash del evento con el hash de la factura completa
      auditLog.hash = invoiceHash;
      // Recalcular la firma con el nuevo hash
      const eventData = JSON.stringify({
        action: auditLog.action,
        userId: auditLog.userId,
        timestamp: auditLog.timestamp,
        details: auditLog.details,
        ipAddress: auditLog.ipAddress,
        userAgent: auditLog.userAgent,
      });
      const signatureData = `${auditLog.hash}:${eventData}`;
      auditLog.signature = await cryptoService.generateHash(signatureData);

      invoice.auditTrail.push(auditLog);

      // Preparar datos para Firestore
      const firestoreData: any = {
        userId: invoice.userId,
        workshopId: invoice.workshopId,
        participantId: invoice.participantId,
        invoiceNumber: invoice.invoiceNumber,
        invoiceDate: invoice.invoiceDate,
        dueDate: invoice.dueDate,
        participantInfo: invoice.participantInfo,
        workshopInfo: invoice.workshopInfo,
        companyInfo: invoice.companyInfo,
        items: invoice.items,
        subtotal: invoice.subtotal,
        taxAmount: invoice.taxAmount,
        total: invoice.total,
        currency: invoice.currency,
        status: invoice.status,
        auditTrail: invoice.auditTrail,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Solo añadir campos opcionales si tienen valor
      if (invoice.paymentMethod) {
        firestoreData.paymentMethod = invoice.paymentMethod;
      }
      if (invoice.paymentDate) {
        firestoreData.paymentDate = invoice.paymentDate;
      }
      if (invoice.stripePaymentIntentId) {
        firestoreData.stripePaymentIntentId = invoice.stripePaymentIntentId;
      }
      if (invoice.stripeSessionId) {
        firestoreData.stripeSessionId = invoice.stripeSessionId;
      }
      if (invoice.notes) {
        firestoreData.notes = invoice.notes;
      }
      if (invoice.terms) {
        firestoreData.terms = invoice.terms;
      }
      if (invoice.xmlContent) {
        firestoreData.xmlContent = invoice.xmlContent;
      }
      if (invoice.pdfUrl) {
        firestoreData.pdfUrl = invoice.pdfUrl;
      }

      // Guardar en Firestore
      const docRef = await addDoc(
        collection(db, 'workshopInvoices'),
        firestoreData
      );

      // Actualizar el participante con el ID de la factura
      if (participant.id) {
        await updateDoc(doc(db, 'workshopParticipants', participant.id), {
          invoiceId: docRef.id,
          updatedAt: serverTimestamp(),
        });
      }

      return {
        ...invoice,
        id: docRef.id,
      };
    } catch (error) {
      console.error('Error creando factura de taller:', error);
      throw error;
    }
  }

  // Obtener todas las facturas de talleres de un usuario
  async getWorkshopInvoicesByUser(userId: string): Promise<WorkshopInvoice[]> {
    try {
      const invoicesRef = collection(db, 'workshopInvoices');
      const q = query(
        invoicesRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        invoiceDate: doc.data().invoiceDate?.toDate() || new Date(),
        dueDate: doc.data().dueDate?.toDate() || new Date(),
        paymentDate: doc.data().paymentDate?.toDate(),
        workshopInfo: {
          ...doc.data().workshopInfo,
          date: doc.data().workshopInfo?.date?.toDate() || new Date(),
        },
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      })) as WorkshopInvoice[];
    } catch (error) {
      console.error('Error obteniendo facturas de talleres:', error);
      throw error;
    }
  }

  // Obtener una factura de taller por ID
  async getWorkshopInvoiceById(
    invoiceId: string
  ): Promise<WorkshopInvoice | null> {
    try {
      const docRef = doc(db, 'workshopInvoices', invoiceId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        invoiceDate: data.invoiceDate?.toDate() || new Date(),
        dueDate: data.dueDate?.toDate() || new Date(),
        paymentDate: data.paymentDate?.toDate(),
        workshopInfo: {
          ...data.workshopInfo,
          date: data.workshopInfo?.date?.toDate() || new Date(),
        },
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as WorkshopInvoice;
    } catch (error) {
      console.error('Error obteniendo factura de taller:', error);
      throw error;
    }
  }

  // Actualizar estado de pago de una factura
  async updatePaymentStatus(
    invoiceId: string,
    status: 'paid' | 'sent' | 'overdue' | 'cancelled',
    paymentMethod?: string,
    paymentDate?: Date,
    stripePaymentIntentId?: string,
    stripeSessionId?: string
  ): Promise<void> {
    try {
      const docRef = doc(db, 'workshopInvoices', invoiceId);
      const updateData: any = {
        status,
        updatedAt: serverTimestamp(),
      };

      if (paymentMethod) {
        updateData.paymentMethod = paymentMethod;
      }
      if (paymentDate) {
        updateData.paymentDate = paymentDate;
      }
      if (stripePaymentIntentId) {
        updateData.stripePaymentIntentId = stripePaymentIntentId;
      }
      if (stripeSessionId) {
        updateData.stripeSessionId = stripeSessionId;
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error actualizando estado de pago:', error);
      throw error;
    }
  }
}

export const workshopInvoiceService = WorkshopInvoiceService.getInstance();

